import type { RouteRecordRaw } from "vue-router";
import { ADMIN_ROUTES } from "../utils/admin-routes";
import { TASKS_ROUTE_NAME } from "../utils/route-names";

const adminMeta = {
  requiresAuth: true,
  requiresAdmin: true,
  layout: "AppLayout",
} as const;

export const NAVIGATION_ERROR_ROUTE_NAME = "navigation-error";
export const NAVIGATION_NOT_FOUND_ROUTE_NAME = "navigation-not-found";

export const navigationErrorRoutes: RouteRecordRaw[] = [
  {
    path: "/error/:code(\\d+)?",
    name: NAVIGATION_ERROR_ROUTE_NAME,
    component: () => import("../pages/NavigationErrorPage.vue"),
    meta: {
      requiresAuth: false,
      layout: "AppLayout",
      headerTitleKey: "common.error",
      errorCode: 500,
    },
  },
  {
    path: "/:pathMatch(.*)*",
    name: NAVIGATION_NOT_FOUND_ROUTE_NAME,
    component: () => import("../pages/NavigationErrorPage.vue"),
    meta: {
      requiresAuth: false,
      layout: "AppLayout",
      headerTitleKey: "common.error",
      errorCode: 404,
    },
  },
];

const appRoutes: RouteRecordRaw[] = [
  {
    path: "/welcome",
    component: () => import("../pages/OnboardingWelcomePage.vue"),
    meta: {
      requiresAuth: true,
    },
  },
  // Backwards compatible layer for app versions 0.3.7 -> 0.3.8 which redirects to '/tasks/' which doesn't exist in latest frontend OTA
  {
    path: "/tasks/",
    redirect: "/tasks",
  },
  {
    path: "/tasks",
    name: TASKS_ROUTE_NAME,
    component: () => import("../pages/TasksPage.vue"),
    meta: {
      requiresAuth: true,
      layout: "AppLayout",
      headerTitleKey: "common.your_tasks",
    },
  },
  {
    path: "/tasks/history",
    component: () => import("../pages/HistoryPage.vue"),
    meta: {
      requiresAuth: true,
      layout: "AppLayout",
      headerTitleKey: "tasks.history",
    },
  },
  {
    // The training applies to every user, so the agent needs the route too.
    // It brings its own full-screen wizard chrome instead of AppLayout.
    path: "/tasks/spot-the-phish",
    component: () => import("../pages/SpotThePhishPage.vue"),
    meta: {
      requiresAuth: true,
      headerTitleKey: "spot_the_phish_page.title",
    },
  },
  {
    path: "/tasks/speak-binary",
    component: () => import("../pages/SpeakBinaryPage.vue"),
    meta: {
      requiresAuth: true,
      headerTitleKey: "shared_ui.tasks.speak_binary.title",
    },
  },
  {
    path: "/settings",
    component: () => import("../pages/SettingsPage.vue"),
    meta: {
      requiresAuth: true,
      layout: "AppLayout",
      headerTitleKey: "common.settings",
    },
  },
  {
    path: "/help",
    component: () => import("../pages/HelpPage.vue"),
    meta: {
      requiresAuth: false,
      layout: "AppLayout",
      headerTitleKey: "help.title",
    },
  },
  {
    path: "/integrations",
    redirect: (to) => ({ path: ADMIN_ROUTES.systems, query: to.query }),
    meta: adminMeta,
  },
  {
    path: "/users",
    redirect: ADMIN_ROUTES.employees,
    meta: adminMeta,
  },
  {
    path: "/team-progress",
    redirect: ADMIN_ROUTES.overview,
    meta: adminMeta,
  },
  {
    path: "/risk-analysis",
    component: () => import("../pages/RiskAnalysisPage.vue"),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      layout: "AppLayout",
      headerTitleKey: "risk_analysis.title",
    },
  },
  {
    path: "/backup",
    component: () => import("../pages/BackupPage.vue"),
    meta: {
      requiresAuth: true,
      layout: "AppLayout",
    },
  },
  {
    path: "/admin",
    redirect: ADMIN_ROUTES.overview,
  },
  {
    path: ADMIN_ROUTES.overview,
    component: () => import("../pages/AdminOverviewPage.vue"),
    meta: { ...adminMeta, headerTitleKey: "admin_overview.title" },
  },
  {
    path: ADMIN_ROUTES.employees,
    component: () => import("../pages/AdminEmployeesPage.vue"),
    meta: { ...adminMeta, headerTitleKey: "admin_employees.title" },
  },
  {
    path: ADMIN_ROUTES.employeesAdd,
    component: () => import("../pages/AdminAddEmployeesPage.vue"),
    meta: { ...adminMeta, headerTitleKey: "admin_add_employees.title" },
  },
  {
    path: `${ADMIN_ROUTES.employees}/:id`,
    component: () => import("../pages/AdminEmployeeDetailPage.vue"),
    meta: { ...adminMeta, headerTitleKey: "admin_employees.title" },
  },
  {
    path: `${ADMIN_ROUTES.employees}/:id/edit`,
    component: () => import("../pages/AdminEditEmployeePage.vue"),
    meta: { ...adminMeta, headerTitleKey: "admin_edit_employee.title" },
  },
  {
    path: ADMIN_ROUTES.systems,
    component: () => import("../pages/AdminSystemsPage.vue"),
    meta: { ...adminMeta, headerTitleKey: "admin_systems.title" },
  },
  {
    path: ADMIN_ROUTES.addSystem,
    component: () => import("../pages/AdminAddSystemPage.vue"),
    meta: { ...adminMeta, headerTitleKey: "admin_add_system.title" },
  },
  {
    path: `${ADMIN_ROUTES.systems}/:id`,
    component: () => import("../pages/AdminSystemDetailPage.vue"),
    meta: { ...adminMeta, headerTitleKey: "admin_systems.title" },
  },
  {
    path: ADMIN_ROUTES.devices,
    component: () => import("../pages/AdminDevicesPage.vue"),
    meta: { ...adminMeta, headerTitleKey: "device_inventory.title" },
  },
  {
    path: ADMIN_ROUTES.devicesAdd,
    component: () => import("../pages/AdminDeviceFormPage.vue"),
    meta: { ...adminMeta, headerTitleKey: "admin_add_device.title" },
  },
  {
    path: `${ADMIN_ROUTES.devices}/:id`,
    component: () => import("../pages/AdminDeviceDetailPage.vue"),
    meta: { ...adminMeta, headerTitleKey: "device_inventory.title" },
  },
  {
    path: `${ADMIN_ROUTES.devices}/:id/edit`,
    component: () => import("../pages/AdminDeviceFormPage.vue"),
    meta: { ...adminMeta, headerTitleKey: "admin_edit_device.title" },
  },
  {
    path: ADMIN_ROUTES.insuranceRequirements,
    component: () => import("../pages/AdminInsuranceRequirementsPage.vue"),
    meta: { ...adminMeta, headerTitleKey: "admin_insurance.title" },
  },
  {
    path: "/tasks/the-checklist",
    component: () => import("../pages/TheChecklistPage.vue"),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      headerTitleKey: "the_checklist_page.title",
    },
  },
];

export const sharedRoutes: RouteRecordRaw[] = [
  ...appRoutes,
  ...navigationErrorRoutes,
];
